import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertOctagon,
  BarChart3,
  Bell,
  BookOpenCheck,
  CheckCircle2,
  Film,
  Package,
  Users,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtDateBR, tr, CHECKIN_METHOD_LABEL } from "@/lib/labels";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import {
  ANALYTICS_COLORS,
  AnalyticsDrawer,
  AnalyticsTabs,
  AttendanceHeatmap,
  ChartCard,
  EmptyAnalytics,
  MetricCard,
  formatPercent,
} from "@/components/analytics/AnalyticsPrimitives";

const BUCKET_COLOR = {
  PERFECT: ANALYTICS_COLORS.green,
  HIGH: "#4F8F63",
  ATTENTION: ANALYTICS_COLORS.amber,
  CRITICAL: ANALYTICS_COLORS.red,
  NO_DATA: "#A39A8E",
};

const TABS = [
  { value: "overview", label: "Visão geral" },
  { value: "presence", label: "Presença & frequência" },
  { value: "engagement", label: "Engajamento" },
];

const conditionLabel = {
  ALL: "Todos",
  PERFECT: "100% presente",
  ATTENTION: "Atenção",
  CRITICAL: "Crítico",
  JUSTIFIED: "Com justificativa",
};

function LoadingAnalytics() {
  return (
    <div className="space-y-4 animate-pulse" aria-label="Carregando analytics">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((n) => <div key={n} className="h-28 rounded-2xl bg-[#F0EBE3]" />)}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="h-80 rounded-2xl bg-[#F0EBE3]" />
        <div className="h-80 rounded-2xl bg-[#F0EBE3]" />
      </div>
    </div>
  );
}

function SimpleTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E0D9CD] rounded-xl shadow-lg px-3 py-2 text-xs max-w-[220px]">
      {label != null && <p className="font-semibold mb-1 break-words">{label}</p>}
      {payload.map((item) => (
        <p key={`${item.dataKey}-${item.name}`} className="text-[#6B655E]">
          <span className="font-medium text-[#1F1F1F]">{item.name}:</span> {item.value}
        </p>
      ))}
    </div>
  );
}

export default function ClassAnalyticsView({ classGroupId, compactHeader = false }) {
  const [report, setReport] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [condition, setCondition] = useState("ALL");
  const [drawer, setDrawer] = useState(null);

  useEffect(() => {
    if (!classGroupId) return;
    let alive = true;
    setLoading(true);
    setError("");
    setDrawer(null);
    setCondition("ALL");
    Promise.all([
      api.get(`/reports/class/${classGroupId}`),
      api.get(`/analytics/classes/${classGroupId}`),
    ])
      .then(([reportResponse, analyticsResponse]) => {
        if (!alive) return;
        setReport(reportResponse.data);
        setAnalytics(analyticsResponse.data);
      })
      .catch((err) => {
        if (!alive) return;
        setError(formatApiError(err));
        setReport(null);
        setAnalytics(null);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [classGroupId]);

  const analyticsByStudent = useMemo(() => {
    const map = new Map();
    (analytics?.students || []).forEach((row) => map.set(row.student_id, row));
    return map;
  }, [analytics]);

  const filteredStudents = useMemo(() => {
    const rows = report?.presence?.rows || [];
    return rows.filter((row) => {
      if (condition === "ALL") return true;
      if (condition === "JUSTIFIED") return (row.justified || 0) > 0;
      const a = analyticsByStudent.get(row.student_id);
      if (condition === "PERFECT") return a?.bucket === "PERFECT";
      if (condition === "CRITICAL") return a?.bucket === "CRITICAL";
      if (condition === "ATTENTION") return ["HIGH", "ATTENTION"].includes(a?.bucket);
      return true;
    });
  }, [report, analyticsByStudent, condition]);

  const bucketStudents = (key) => (analytics?.students || []).filter((row) => row.bucket === key);

  const openBucket = (bucket) => {
    setDrawer({ type: "bucket", bucket, students: bucketStudents(bucket.key) });
  };

  const openLesson = (lessonTrend) => {
    setDrawer({ type: "lesson", lesson: lessonTrend });
  };

  if (!classGroupId) return <EmptyAnalytics title="Selecione uma turma" description="Escolha uma turma para carregar os indicadores e gráficos." />;
  if (loading) return <LoadingAnalytics />;
  if (error) return <EmptyAnalytics title="Não foi possível carregar os dados" description={error} />;
  if (!report || !analytics) return <EmptyAnalytics />;

  const summary = analytics.summary || {};
  const presence = report.presence || {};
  const trend = analytics.lesson_trend || [];
  const distribution = analytics.frequency_distribution || [];
  const videoRows = report.video?.rows || [];
  const materialRows = report.materials?.rows || [];
  const notifications = report.notifications || {};
  const pushPie = [
    { name: "Com push", value: notifications.with_push || 0, fill: ANALYTICS_COLORS.green },
    { name: "Sem push", value: notifications.without_push || 0, fill: ANALYTICS_COLORS.amber },
    ...(notifications.blocked ? [{ name: "Bloqueados", value: notifications.blocked, fill: ANALYTICS_COLORS.red }] : []),
  ].filter((item) => item.value > 0);

  return (
    <div className="space-y-5" data-testid="class-analytics-view">
      {!compactHeader && (
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#6B655E] font-medium">Analytics da turma</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1">{analytics.class_group?.name || report.class_group?.name}</h2>
            <p className="text-sm text-[#6B655E] mt-1">Leitura visual para identificar padrão, risco e oportunidade de ação.</p>
          </div>
          <div className="text-xs text-[#6B655E] rounded-xl bg-[#F6EFE4] px-3 py-2">Atualização sob demanda</div>
        </div>
      )}

      <AnalyticsTabs value={tab} onChange={setTab} items={TABS} />

      {tab === "overview" && (
        <div className="space-y-4" role="tabpanel">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <MetricCard label="Alunos ativos" value={summary.active_students ?? 0} icon={Users} tone="orange" />
            <MetricCard label="Presença média" value={formatPercent(summary.average_presence)} icon={CheckCircle2} tone="success" />
            <MetricCard
              label="Alunos críticos"
              value={summary.critical_students ?? 0}
              hint="Abaixo de 75%"
              icon={AlertOctagon}
              tone="critical"
              onClick={() => openBucket(distribution.find((b) => b.key === "CRITICAL") || { key: "CRITICAL", label: "Abaixo de 75%" })}
            />
            <MetricCard label="Aulas válidas" value={summary.valid_lessons ?? 0} icon={BookOpenCheck} tone="info" />
            <MetricCard label="Justificativas" value={presence.justified_total ?? 0} icon={BarChart3} tone="warning" />
          </div>

          <div className="grid xl:grid-cols-2 gap-4">
            <ChartCard title="Evolução da presença" subtitle="Percentual da turma em cada aula válida. Clique em um ponto para abrir o detalhe da aula.">
              {trend.length ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trend} margin={{ top: 8, right: 10, left: -18, bottom: 6 }} accessibilityLayer>
                      <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" />
                      <XAxis dataKey="lesson_date" tickFormatter={(v) => fmtDateBR(v)} tick={{ fontSize: 11 }} minTickGap={24} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip content={<SimpleTooltip />} labelFormatter={(v) => fmtDateBR(v)} />
                      <Line
                        type="monotone"
                        dataKey="percent"
                        name="Presença"
                        stroke={ANALYTICS_COLORS.orange}
                        strokeWidth={3}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6, onClick: (_, payload) => openLesson(payload?.payload) }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyAnalytics description="Ainda não há aulas válidas para gerar tendência." />}
            </ChartCard>

            <ChartCard title="Distribuição de frequência" subtitle="Quantos alunos estão em cada faixa. Clique em uma barra para ver os nomes.">
              {distribution.some((row) => row.count > 0) ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={distribution} margin={{ top: 8, right: 8, left: -18, bottom: 8 }} accessibilityLayer>
                      <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip content={<SimpleTooltip />} />
                      <Bar dataKey="count" name="Alunos" radius={[8, 8, 0, 0]} onClick={(row) => openBucket(row)} cursor="pointer">
                        {distribution.map((row) => <Cell key={row.key} fill={BUCKET_COLOR[row.key] || ANALYTICS_COLORS.orange} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyAnalytics />}
            </ChartCard>
          </div>

          <ChartCard title="Leitura rápida" subtitle="Indicadores que merecem atenção sem precisar abrir todas as listas.">
            <div className="grid sm:grid-cols-3 gap-3">
              <button type="button" onClick={() => openBucket(distribution.find((b) => b.key === "CRITICAL") || { key: "CRITICAL", label: "Críticos" })} className="rounded-xl border border-[#F0B6B6] bg-[#FFF7F7] p-4 text-left hover:shadow-sm">
                <p className="text-xs text-[#6B655E]">Risco de frequência</p>
                <p className="font-display text-2xl font-bold mt-1 text-[#C53030]">{summary.critical_students || 0}</p>
                <p className="text-xs text-[#6B655E] mt-1">aluno(s) abaixo de 75% · ver detalhes</p>
              </button>
              <button type="button" onClick={() => setTab("presence")} className="rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] p-4 text-left hover:shadow-sm">
                <p className="text-xs text-[#6B655E]">Mapa de presença</p>
                <p className="font-display text-lg font-bold mt-1">Aluno × aula</p>
                <p className="text-xs text-[#6B655E] mt-1">Encontre padrões de falta no heatmap.</p>
              </button>
              <button type="button" onClick={() => setTab("engagement")} className="rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] p-4 text-left hover:shadow-sm">
                <p className="text-xs text-[#6B655E]">Engajamento</p>
                <p className="font-display text-lg font-bold mt-1">Vídeo, material e push</p>
                <p className="text-xs text-[#6B655E] mt-1">Veja o que está chegando e sendo concluído.</p>
              </button>
            </div>
          </ChartCard>
        </div>
      )}

      {tab === "presence" && (
        <div className="space-y-4" role="tabpanel">
          <ChartCard title="Presença por aula" subtitle="Presentes, faltas e justificadas em cada encontro. Clique em uma barra para abrir o detalhe.">
            {trend.length ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trend} margin={{ top: 8, right: 8, left: -18, bottom: 8 }} accessibilityLayer>
                    <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="lesson_date" tickFormatter={(v) => fmtDateBR(v)} tick={{ fontSize: 11 }} minTickGap={24} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip content={<SimpleTooltip />} labelFormatter={(v) => fmtDateBR(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="present" name="Presentes" stackId="attendance" fill={ANALYTICS_COLORS.green} onClick={(row) => openLesson(row)} cursor="pointer" />
                    <Bar dataKey="justified" name="Justificadas" stackId="attendance" fill={ANALYTICS_COLORS.amber} onClick={(row) => openLesson(row)} cursor="pointer" />
                    <Bar dataKey="absent" name="Faltas" stackId="attendance" fill={ANALYTICS_COLORS.red} onClick={(row) => openLesson(row)} cursor="pointer" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <EmptyAnalytics />}
          </ChartCard>

          <ChartCard title="Mapa aluno × aula" subtitle="P = presente · J = justificada · F = falta. Toque em uma célula para abrir o contexto.">
            <AttendanceHeatmap
              lessons={analytics.lessons || []}
              students={analytics.students || []}
              onCellClick={(payload) => setDrawer({ type: "cell", ...payload })}
            />
          </ChartCard>

          <ChartCard
            title="Alunos da turma"
            subtitle="Filtre por condição e vá direto ao detalhe individual."
            action={(
              <DoxaSelect
                value={condition}
                onValueChange={setCondition}
                options={Object.entries(conditionLabel).map(([value, label]) => ({ value, label }))}
                ariaLabel="Filtrar alunos por condição"
                size="compact"
                className="min-w-[170px] text-xs"
              />
            )}
          >
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {filteredStudents.map((row) => (
                <Link key={row.student_id} to={`/students/${row.student_id}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#EBE5DB] px-3 py-2.5 hover:bg-[#FAF7F1]">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{row.full_name}</p>
                    <p className="text-xs text-[#6B655E]">{row.present}/{row.considered} consideradas · {row.unjustified_absent || 0} falta(s) · {row.justified || 0} justificada(s)</p>
                  </div>
                  <span className={`font-display text-lg font-bold shrink-0 ${row.percent != null && row.percent < 75 ? "text-[#C53030]" : "text-[#1F1F1F]"}`}>{row.percent == null ? "—" : `${row.percent}%`}</span>
                </Link>
              ))}
              {!filteredStudents.length && <EmptyAnalytics title="Nenhum aluno nesta condição" />}
            </div>
          </ChartCard>
        </div>
      )}

      {tab === "engagement" && (
        <div className="space-y-4" role="tabpanel">
          <div className="grid xl:grid-cols-2 gap-4">
            <ChartCard title="Conclusão de vídeo" subtitle="Conclusão exige 90% assistido e não substitui a presença.">
              {videoRows.length ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={videoRows} layout="vertical" margin={{ top: 6, right: 12, left: 20, bottom: 6 }} accessibilityLayer>
                      <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="title" width={110} tick={{ fontSize: 10 }} />
                      <Tooltip content={<SimpleTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="completed" name="Concluíram" stackId="video" fill={ANALYTICS_COLORS.green} />
                      <Bar dataKey="pending" name="Pendentes" stackId="video" fill={ANALYTICS_COLORS.orange} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyAnalytics description="Nenhuma aula com vídeo nesta turma." />}
            </ChartCard>

            <ChartCard title="Métodos de check-in" subtitle="Como as presenças válidas foram registradas nas aulas da turma.">
              {(analytics.checkin_methods || []).length ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.checkin_methods} margin={{ top: 8, right: 8, left: -18, bottom: 8 }} accessibilityLayer>
                      <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="method" tickFormatter={(v) => tr(CHECKIN_METHOD_LABEL, v)} tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip content={<SimpleTooltip />} labelFormatter={(v) => tr(CHECKIN_METHOD_LABEL, v)} />
                      <Bar dataKey="count" name="Check-ins" fill={ANALYTICS_COLORS.orange} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyAnalytics description="Ainda não há check-ins válidos registrados." />}
            </ChartCard>
          </div>

          <div className="grid xl:grid-cols-2 gap-4">
            <ChartCard title="Materiais físicos" subtitle="Recebidos x pendentes por material." action={<Package className="w-4 h-4 text-[#F75A1B]" />}>
              {materialRows.length ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={materialRows} layout="vertical" margin={{ top: 6, right: 12, left: 20, bottom: 6 }} accessibilityLayer>
                      <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="title" width={110} tick={{ fontSize: 10 }} />
                      <Tooltip content={<SimpleTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="received" name="Recebidos" stackId="material" fill={ANALYTICS_COLORS.green} />
                      <Bar dataKey="pending" name="Pendentes" stackId="material" fill={ANALYTICS_COLORS.amber} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyAnalytics description="Nenhum material físico ativo nesta turma." />}
            </ChartCard>

            <ChartCard title="Ativação de notificações" subtitle="Adesão ao Web Push entre alunos ativos." action={<Bell className="w-4 h-4 text-[#F75A1B]" />}>
              {pushPie.length ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart accessibilityLayer>
                      <Tooltip content={<SimpleTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Pie data={pushPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                        {pushPie.map((item) => <Cell key={item.name} fill={item.fill} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyAnalytics description="Sem dados de notificações para esta turma." />}
            </ChartCard>
          </div>
        </div>
      )}

      <AnalyticsDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        title={drawer?.type === "bucket" ? drawer?.bucket?.label || "Faixa de frequência" : drawer?.type === "lesson" ? drawer?.lesson?.title || "Aula" : drawer?.type === "cell" ? drawer?.student?.full_name || "Aluno" : "Detalhe"}
        subtitle={drawer?.type === "lesson" ? fmtDateBR(drawer?.lesson?.lesson_date) : drawer?.type === "cell" ? `${drawer?.lesson?.title || "Aula"} · ${fmtDateBR(drawer?.lesson?.lesson_date)}` : undefined}
      >
        {drawer?.type === "bucket" && (
          <div className="space-y-2">
            <p className="text-sm text-[#6B655E] mb-3">{drawer.students?.length || 0} aluno(s) nesta faixa.</p>
            {(drawer.students || []).map((student) => (
              <Link key={student.student_id} to={`/students/${student.student_id}`} className="flex items-center justify-between rounded-xl border border-[#EBE5DB] px-3 py-2.5 hover:bg-[#FAF7F1]">
                <span className="text-sm font-medium truncate">{student.full_name}</span>
                <span className="font-display font-bold">{student.percent == null ? "—" : `${student.percent}%`}</span>
              </Link>
            ))}
            {!drawer.students?.length && <EmptyAnalytics title="Nenhum aluno nesta faixa" />}
          </div>
        )}
        {drawer?.type === "lesson" && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-[#E6F4EA] p-3 text-center"><p className="font-display text-xl font-bold text-[#2F855A]">{drawer.lesson.present || 0}</p><p className="text-[11px] text-[#6B655E]">Presentes</p></div>
              <div className="rounded-xl bg-[#FEEBC8] p-3 text-center"><p className="font-display text-xl font-bold text-[#C05621]">{drawer.lesson.justified || 0}</p><p className="text-[11px] text-[#6B655E]">Justificadas</p></div>
              <div className="rounded-xl bg-[#FED7D7] p-3 text-center"><p className="font-display text-xl font-bold text-[#C53030]">{drawer.lesson.absent || 0}</p><p className="text-[11px] text-[#6B655E]">Faltas</p></div>
            </div>
            <p className="text-sm">Presença da aula: <strong>{formatPercent(drawer.lesson.percent)}</strong></p>
            <Link to={`/lessons/${drawer.lesson.lesson_id}`} className="doxa-btn-primary px-4 py-2.5 text-sm inline-flex">Abrir detalhe da aula</Link>
          </div>
        )}
        {drawer?.type === "cell" && (
          <div className="space-y-4">
            <div className="rounded-xl bg-[#FAF7F1] border border-[#EBE5DB] p-4">
              <p className="text-xs text-[#6B655E]">Situação</p>
              <p className="font-display text-2xl font-bold mt-1">{drawer.cell?.status === "PRESENT" ? "Presente" : drawer.cell?.status === "JUSTIFIED" ? "Falta justificada" : "Falta"}</p>
              {drawer.cell?.checkin_method && <p className="text-xs text-[#6B655E] mt-2">Check-in: {tr(CHECKIN_METHOD_LABEL, drawer.cell.checkin_method)}</p>}
            </div>
            <Link to={`/students/${drawer.student?.student_id}`} className="text-sm text-[#F75A1B] font-medium hover:underline">Abrir resumo do aluno</Link>
            <br />
            <Link to={`/lessons/${drawer.lesson?.id}`} className="text-sm text-[#F75A1B] font-medium hover:underline">Abrir detalhe da aula</Link>
          </div>
        )}
      </AnalyticsDrawer>
    </div>
  );
}
